/**
 * The COLOUR STREAM (2026-11-02, operator: "on timelines, add a button to change the view (keyframes and blocks) into a
 * stream of colour, representing what colour is shown on every frame. so each line will be a flowing colour from start
 * to end, so if off, show black").
 *
 * What each light SHOWS at every moment, worked out with the maths that plays it (`lightMix.ts`, `colorSample.ts`): its
 * own keyframes and pattern blocks, the masters that take it over (with their fades in and back), mute = off, and a
 * timeline that starts dark. Pure, so a smoke can pin it; the editor draws it (TimelineV2's Stream view).
 *
 * A stream is RUNS over bars: each a stretch holding one colour, or a fade sampled along it. Runs are cut at every
 * key, takeover edge and fade edge, so a cut lands on its beat however coarse the sampling. A `null` colour means
 * nothing drives the light there (the timeline leaves it as it was), which is drawn apart from black: black is a light
 * that is OFF.
 */
import type { TimelineLane } from '@opbuttons/schema';
import { isColorMarkerStep, unrollPatterns } from '@opbuttons/schema';
import type { Rgb01 } from './colorSample.ts';
import { lightColourWith, ownColourOf, takeoverColour, takeoverWeight, takeoversOf, type Takeover } from './lightMix.ts';

/** A colour in the stream; `a` (0..1, absent = 1) is how strongly it is driven where that is partial: a master fading
 *  in over a light its own lane leaves alone, or a master's own lane through its fades. */
export type StreamColour = Rgb01 & { a?: number };

/** One stretch, `start` to `end` in bars: one colour held, or several evenly spaced along a fade. */
export interface StreamRun { start: number; end: number; colours: Array<StreamColour | null> }

/** Colours closer than this in every channel are the same (well under one 8-bit step). */
const SAME = 1 / 1024;
/** The most samples one fade gets, however wide it is drawn. */
const MAX_SAMPLES = 256;

function same(a: StreamColour | null, b: StreamColour | null): boolean {
  if (!a || !b) return a === b;
  return Math.abs(a.r - b.r) < SAME && Math.abs(a.g - b.g) < SAME && Math.abs(a.b - b.b) < SAME
    && Math.abs((a.w ?? 0) - (b.w ?? 0)) < SAME && Math.abs((a.a ?? 1) - (b.a ?? 1)) < SAME;
}

/**
 * Sample `at` over 0..`total` bars into runs. `edges` are the moments where the colour may jump or a fade may start or
 * end; between two edges the colour is one hold or one smooth change, so three samples say whether it holds, and a
 * change is sampled every `step` bars (the caller's few pixels).
 */
export function streamRuns(at: (pos: number) => StreamColour | null, edges: readonly number[], total: number, step: number): StreamRun[] {
  if (!(total > 0)) return [];
  const xs = [0, total, ...edges.filter((p) => p > 0 && p < total)].sort((a, b) => a - b);
  const cuts: number[] = [];
  for (const x of xs) if (cuts.length === 0 || x - cuts[cuts.length - 1]! > 1e-9) cuts.push(x);
  const out: StreamRun[] = [];
  for (let i = 0; i < cuts.length - 1; i++) {
    const p = cuts[i]!; const q = cuts[i + 1]!;
    const lastAt = q - Math.min(1e-6, (q - p) / 2);
    const first = at(p); const mid = at((p + q) / 2); const last = at(lastAt);
    if (same(first, mid) && same(mid, last)) {
      const prev = out[out.length - 1];
      if (prev && prev.colours.length === 1 && same(prev.colours[0]!, first) && Math.abs(prev.end - p) < 1e-9) prev.end = q;
      else out.push({ start: p, end: q, colours: [first] });
      continue;
    }
    const n = Math.max(3, Math.min(MAX_SAMPLES, Math.ceil((q - p) / Math.max(step, 1e-6)) + 1));
    const colours: Array<StreamColour | null> = [];
    for (let j = 0; j < n; j++) colours.push(j === n - 1 ? last : at(p + ((q - p) * j) / (n - 1)));
    out.push({ start: p, end: q, colours });
  }
  return out;
}

function takeoverEdges(tks: readonly Takeover[]): number[] {
  const out: number[] = [];
  for (const tk of tks) {
    out.push(tk.start - tk.leadIn, tk.start, tk.end - tk.release, tk.end);
    for (const k of tk.keys) out.push(k.position);
  }
  return out;
}

/**
 * What ONE light shows (a `dmx-fixture` lane), as it plays: `own.lane` with its steps as they play (the lead-in applied,
 * a dark start's Off key in), and the masters that reach it (`mastersReaching`, highest first), likewise as they play.
 * Its own lane muted = off, except where a master takes it over (`lightColourAt`'s rules).
 */
export function lightStream(
  own: { lane: TimelineLane; audible: boolean },
  masters: ReadonlyArray<{ lane: TimelineLane; audible: boolean }>,
  total: number,
  step: number,
): StreamRun[] {
  const ownAt = ownColourOf(own.lane);
  const ms = masters.map((m) => ({ takeovers: m.audible ? takeoversOf(m.lane, total) : [], audible: m.audible }));
  const at = (pos: number): StreamColour | null => {
    const r = lightColourWith({ at: ownAt, audible: own.audible }, ms, pos);
    if (!r) return null;
    // Its own lane says nothing here: a master fading in fades from whatever the light was, which only the rig knows.
    return r.alone && r.over < 1 ? { ...r.colour, a: r.over } : r.colour;
  };
  const edges = [
    ...unrollPatterns(own.lane.steps).filter(isColorMarkerStep).map((k) => k.position),
    ...ms.flatMap((m) => takeoverEdges(m.takeovers)),
  ];
  return streamRuns(at, edges, total, step);
}

/** What an "All lights" master (`dmx-rig`) sends: its colour wherever it takes over, as strong as its fades say, and
 *  nothing (`null`) where it leaves the lights to their own lanes. A muted master takes nothing over. */
export function masterStream(master: TimelineLane, audible: boolean, total: number, step: number): StreamRun[] {
  const tks = audible ? takeoversOf(master, total) : [];
  const at = (pos: number): StreamColour | null => {
    for (const tk of tks) {
      const w = takeoverWeight(tk, pos);
      if (w > 0) return w < 1 ? { ...takeoverColour(tk, pos), a: w } : takeoverColour(tk, pos);
    }
    return null;
  };
  return streamRuns(at, takeoverEdges(tks), total, step);
}

/** The colour a light gives off: its white LEDs add white over the colour (the editor's convention for `w`). */
export function emitted(c: StreamColour): StreamColour {
  const w = c.w ?? 0;
  if (!(w > 0)) return c;
  return { r: Math.min(1, c.r + w), g: Math.min(1, c.g + w), b: Math.min(1, c.b + w), ...(c.a !== undefined ? { a: c.a } : {}) };
}

function css(c: StreamColour | null): string {
  if (!c) return 'transparent';
  const e = emitted(c);
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v * 255)));
  const a = e.a ?? 1;
  return a >= 1 ? `rgb(${to(e.r)} ${to(e.g)} ${to(e.b)})` : `rgb(${to(e.r)} ${to(e.g)} ${to(e.b)} / ${Math.max(0, a).toFixed(3)})`;
}

/**
 * The stream as TILES of CSS: `left` and `width` in px, each with its own gradient, so no one element carries a
 * gradient tens of thousands of pixels wide. A tile names every run that reaches into it, with stops relative to its
 * own left edge (stops outside a gradient's length are allowed and shape what is inside).
 */
export function streamTiles(runs: readonly StreamRun[], ppb: number, tilePx = 2048): Array<{ left: number; width: number; background: string }> {
  if (runs.length === 0 || !(ppb > 0)) return [];
  const total = runs[runs.length - 1]!.end;
  const width = total * ppb;
  const out: Array<{ left: number; width: number; background: string }> = [];
  for (let left = 0; left < width - 0.01; left += tilePx) {
    const w = Math.min(tilePx, width - left);
    const b0 = left / ppb; const b1 = (left + w) / ppb;
    const px = (bars: number) => `${(bars * ppb - left).toFixed(2)}px`;
    const stops: string[] = [];
    for (const r of runs) {
      if (r.end <= b0 + 1e-9 || r.start >= b1 - 1e-9) continue;
      if (r.colours.length === 1) { stops.push(`${css(r.colours[0]!)} ${px(r.start)} ${px(r.end)}`); continue; }
      const n = r.colours.length;
      r.colours.forEach((c, j) => stops.push(`${css(c)} ${px(r.start + ((r.end - r.start) * j) / (n - 1))}`));
    }
    if (stops.length === 1) stops.push(stops[0]!);
    out.push({ left, width: w, background: `linear-gradient(90deg, ${stops.join(', ')})` });
  }
  return out;
}
